import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Loader, Modal } from '~/components/blocks';
import { CompletePractitionerCodeMailPanel } from '~/components/partials';
import { Fields } from '~/components/partials/MailSenderForm/types';
import {
  GetSettingsPractitionerDocument,
  RegistrationPractitionerFragment,
  SendSettingsPractitionerCodeMailDocument,
} from '~/graphql/generated/graphql';
import { completeRegistrationModalState } from '~/state/settings_practitioners/atoms';

import { CompleteRegistrationPanel } from './CompleteRegistrationPanel';

export const CompleteRegistrationModal = () => {
  const [error, setError] = useState<string | null>(null);
  const [isSent, setIsSent] = useState(false);
  const state = useAtomValue(completeRegistrationModalState);
  const reset = useResetAtom(completeRegistrationModalState);

  const [
    sendPractitionerCodeMail,
    { data: sendPractitionerCodeMailData, loading, error: sendPractitionerCodeMailError },
  ] = useMutation(SendSettingsPractitionerCodeMailDocument);
  const [getPractitioner, { data }] = useLazyQuery(GetSettingsPractitionerDocument);
  const practitioner = useMemo(() => {
    if (!data?.node) return null;
    const _practitioner = data.node as RegistrationPractitionerFragment;

    return {
      familyName: _practitioner.familyName,
      givenName: _practitioner.givenName,
      username: _practitioner.authentication.username,
    };
  }, [data]);
  const organization = useMemo(() => {
    if (!data?.node) return null;
    const _organization = (data.node as RegistrationPractitionerFragment).organization;

    return {
      name: _organization.name,
      slug: _organization.slug,
    };
  }, [data]);

  const handleClose = useCallback(() => {
    reset();
    setIsSent(false);
  }, [reset]);
  const handleSubmit = useCallback(
    (values: Fields) => {
      if (!state.practitionerId || !state.practitionerCode) return;

      sendPractitionerCodeMail({
        variables: {
          input: {
            practitionerId: state.practitionerId,
            practitionerCode: state.practitionerCode,
            email: values.email,
            mailType: 'registration',
          },
        },
      });
    },
    [sendPractitionerCodeMail, state.practitionerCode, state.practitionerId],
  );
  useEffect(() => {
    if (state.practitionerId) {
      getPractitioner({
        variables: {
          id: state.practitionerId,
        },
      });
    }
  }, [getPractitioner, state.practitionerId]);

  useEffect(() => {
    if (sendPractitionerCodeMailError) {
      setError(sendPractitionerCodeMailError.message);
    }
  }, [sendPractitionerCodeMailError]);

  useEffect(() => {
    if (sendPractitionerCodeMailData) {
      setIsSent(true);
    }
  }, [sendPractitionerCodeMailData]);

  return (
    <Modal open={state.isOpen} size="s" onClose={handleClose}>
      {practitioner && organization && state.practitionerCode ? (
        isSent ? (
          <CompletePractitionerCodeMailPanel />
        ) : (
          <CompleteRegistrationPanel
            error={error}
            sending={loading}
            organization={organization}
            practitioner={practitioner}
            practitionerCode={state.practitionerCode}
            onSend={handleSubmit}
          />
        )
      ) : (
        <Modal.Body>
          <Box height="200px">
            <Loader open inside />
          </Box>
        </Modal.Body>
      )}
    </Modal>
  );
};
